/**
 * @fileoverview Present one lead's paginated business timeline. `intakeApi.js` supplies recorded activity and the component owns filters/page selection. Timeline presentation connects communications and workflow history without becoming a second owner of lead state or triggering investigation work when opened.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ToastContext from '../../ToastContext';
import AppIcon from '../shared/AppIcon';
import { fetchIntakeLeadTimeline } from './intakeApi';
import { formatDate } from './intakeFormatters';
import './IntakeActivityWorkspace.css';

const ACTIVITY_FILTERS = [
  { key: 'all', label: 'All activity' },
  { key: 'pipedrive', label: 'Pipedrive' },
  { key: 'message', label: 'Messages' },
  { key: 'call', label: 'Calls' },
  { key: 'ai', label: 'AI preparation' },
];



function timelinePresentation(item) {
  if (item.kind === 'call') return { icon: 'pi pi-phone', type: 'call' };
  if (item.kind === 'message') return { icon: 'pi pi-envelope', type: 'message' };
  if (item.kind === 'note') return { icon: 'pi pi-pencil', type: 'pipedrive' };
  if (item.kind === 'ai') return { icon: 'pi pi-sparkles', type: 'ai' };
  return { icon: 'pi pi-history', type: item.source?.startsWith('pipedrive') ? 'pipedrive' : 'activity' };
}



function normalizedTimelineItems(items) {
  return (items || []).map((item) => {
    const presentation = timelinePresentation(item);
    return {
      ...item,
      type: item.source?.startsWith('pipedrive') ? 'pipedrive' : presentation.type,
      icon: presentation.icon,
      detail: item.summary || 'Activity recorded without a summary.',
      meta: [item.source_label, item.status, item.direction, item.actor].filter(Boolean).join(' · '),
      time: item.timestamp || item.due_timestamp,
    };
  });
}



export default function IntakeActivityWorkspace({ lead, active = true }) {
  const showToast = useContext(ToastContext);
  const [activityItems, setActivityItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const requestRef = useRef(0);

  useEffect(

  () => {
    requestRef.current += 1;
    setLoaded(false);
    setActivityItems([]);
    setPage(0);
    setPages(0);
    return () => { requestRef.current += 1; };
  }, [lead?.id]);

  const loadActivity = useCallback(

  async (nextPage = 1) => {
    if (!active || !lead?.id) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await fetchIntakeLeadTimeline(lead.id, { page: nextPage, page_size: 100 });
      if (requestId !== requestRef.current) return;
      const items = response.data.timeline_items || [];
      setActivityItems((previous) => nextPage === 1 ? items : Array.from(new Map([...previous, ...items].map((item) => [item.id, item])).values()));
      setPage(response.data.page || nextPage);
      setPages(response.data.pages || 1);
      setLoaded(true);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setLoaded(true);
      console.error('Failed to load Intake activity workspace:', error);
      showToast({ severity: 'error', summary: 'Activity unavailable', detail: 'Failed to load lead activity' });
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [active, lead?.id, showToast]);

  useEffect(() => {
    if (active && !loaded) loadActivity();
  }, [active, loadActivity, loaded]);

  const timelineItems = useMemo(() => normalizedTimelineItems(activityItems), [activityItems]);
  const visibleItems = filter === 'all' ? timelineItems : timelineItems.filter((item) => (
    filter === 'pipedrive' ? item.source?.startsWith('pipedrive') : item.kind === filter
  ));

  return (
    <section className="intake-activity-workspace intake-activity-workspace--history" aria-label="Activity history">
      <header className="intake-activity-overview">
        <div>
          <span className="intake-activity-eyebrow"><AppIcon icon="pi pi-history" aria-hidden="true" /> Context and investigation</span>
          <h2>Activity History</h2>
          <p>Calls, messages, notes, human actions, and grouped AI preparation. The pipeline remains the place to decide what happens next.</p>
        </div>
        <div className="intake-activity-overview__actions">
          <span className="intake-activity-sync"><AppIcon icon="pi pi-sync" aria-hidden="true" /> Pipedrive is the activity record</span>
          <button type="button" className="intake-button" onClick={() => loadActivity(1)} disabled={loading}>
            <AppIcon icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} aria-hidden="true" />
            <span>{loading ? 'Refreshing' : 'Refresh activity'}</span>
          </button>
        </div>
      </header>

      <section className="intake-activity-panel">
        <div className="intake-activity-panel__header">
          <div><h3>Activity timeline</h3><p>Source-normalized lead activity, newest first.</p></div>
          <span>{visibleItems.length} shown</span>
        </div>
        <div className="intake-activity-filters" aria-label="Activity filters">
          {ACTIVITY_FILTERS.map((item) => (
            <button type="button" key={item.key} aria-pressed={filter === item.key} onClick={() => setFilter(item.key)}>{item.label}</button>
          ))}
        </div>
        {loading && !loaded ? <div className="intake-activity-empty">Loading lead activity...</div> : visibleItems.length ? (
          <ol className="intake-human-timeline">
            {visibleItems.map((item) => (
              <li key={item.id} data-type={item.type} data-source={item.source}>
                <span className="intake-human-timeline__icon"><AppIcon icon={item.icon} aria-hidden="true" /></span>
                <div>
                  <div className="intake-human-timeline__topline"><strong>{item.title}</strong><time>{formatDate(item.time)}</time></div>
                  <p>{item.detail}</p>
                  <small>{item.meta}</small>
                  {item.context ? <small className="intake-human-timeline__context">{item.context}</small> : null}
                  {item.children?.length ? (
                    <details className="intake-ai-preparation">
                      <summary>Show {item.children.length} concise preparation update{item.children.length === 1 ? '' : 's'}</summary>
                      <ul>
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <strong>{child.title}</strong>
                            <span>{child.summary}</span>
                            <time>{formatDate(child.timestamp)}</time>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : <div className="intake-activity-empty">No activity matches this view.</div>}
        {page < pages ? (
          <button type="button" className="intake-button" disabled={loading} onClick={() => loadActivity(page + 1)}>
            {loading ? 'Loading activity…' : 'Load older activity'}
          </button>
        ) : null}
      </section>
    </section>
  );
}
